/**
 * ScrollShadowExamples.tsx
 * Comprehensive showcase of HeroUI ScrollShadow component with trading journal context
 * 
 * Demonstrates scroll shadow functionality, orientations, and customization options
 * with practical examples relevant to trading applications
 */

import React, { useState } from 'react';
import { PlaygroundPageLayout } from '../shared';
import { Card, CardHeader, CardBody } from '@enfomentis/ui';
import { Badge } from '@enfomentis/ui';
import { Divider } from '@enfomentis/ui';
import { ScrollShadow } from '@enfomentis/ui';
import { Button } from '@enfomentis/ui';
import { Switch } from '@enfomentis/ui';
import { Chip } from '@enfomentis/ui';

export const ScrollShadowExamples: React.FC = () => {
  const [hideScrollBar, setHideScrollBar] = useState(false);
  const [shadowSize, setShadowSize] = useState(40);

  // Sample data for trading examples
  const tradeHistory = Array.from({ length: 50 }, (_, i) => ({
    id: i + 1,
    symbol: ['AAPL', 'GOOGL', 'MSFT', 'TSLA', 'AMZN'][i % 5],
    type: i % 2 === 0 ? 'BUY' : 'SELL',
    quantity: Math.floor(Math.random() * 1000) + 10,
    price: (Math.random() * 1000 + 50).toFixed(2),
    time: `${9 + Math.floor(i / 10)}:${(i % 10) * 6}${i % 2 === 0 ? '0' : '3'}`,
    profit: (Math.random() - 0.5) * 1000
  }));

  const newsItems = Array.from({ length: 30 }, (_, i) => ({
    id: i + 1,
    title: [
      'Market Opens Higher on Strong Jobs Report',
      'Fed Signals Possible Rate Cut in Q4',
      'Tech Stocks Rally Amid AI Optimism',
      'Oil Prices Surge on Supply Concerns',
      'Gold Reaches New Yearly Highs'
    ][i % 5],
    time: `${i + 5} minutes ago`,
    source: ['Reuters', 'Bloomberg', 'CNBC', 'MarketWatch', 'WSJ'][i % 5]
  }));

  return (
    <PlaygroundPageLayout>
      <div className="space-y-8">
        
        {/* Header */}
        <div className="text-center space-y-4">
          <div className="inline-flex items-center gap-3">
            <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
              <div className="w-6 h-6 bg-linear-to-b from-transparent via-primary-300 to-transparent rounded"></div>
            </div>
            <h1 className="text-3xl font-bold text-text-primary">ScrollShadow Component</h1>
          </div>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            Visual scroll indicators that dynamically show shadows based on scroll position.
            Perfect for highlighting scrollable content and improving user experience.
          </p>
          <div className="flex items-center justify-center gap-2">
            <Badge color="primary" variant="flat">HeroUI Based</Badge>
            <Badge color="secondary" variant="flat">Layout Component</Badge>
            <Badge color="success" variant="flat">Theme Integrated</Badge>
          </div>
        </div>

        {/* Interactive Controls */}
        <Card>
          <CardHeader>
            <div className="space-y-2">
              <h2 className="text-xl font-semibold text-text-primary">Interactive Controls</h2>
              <p className="text-text-secondary">Customize scroll shadow behavior</p>
            </div>
          </CardHeader>
          <CardBody className="space-y-4">
            <div className="flex flex-wrap items-center gap-6">
              <div className="flex items-center gap-3">
                <Switch 
                  isSelected={hideScrollBar}
                  onValueChange={setHideScrollBar}
                >
                  Hide Scroll Bar
                </Switch>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm text-text-secondary">Shadow Size:</span>
                <div className="flex gap-2">
                  {[20, 40, 60].map((size) => (
                    <Button
                      key={size}
                      size="sm"
                      variant={shadowSize === size ? "solid" : "bordered"}
                      color="primary"
                      onPress={() => setShadowSize(size)}
                    >
                      {size}px
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Vertical Scrolling Examples */}
        <Card>
          <CardHeader>
            <div className="space-y-2">
              <h2 className="text-xl font-semibold text-text-primary">Vertical Scrolling</h2>
              <p className="text-text-secondary">Most common use case with vertical content</p>
            </div>
          </CardHeader>
          <CardBody className="space-y-6">
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              
              {/* Trade History Example */}
              <div className="space-y-3">
                <h3 className="text-lg font-medium text-text-primary">Trade History</h3>
                <ScrollShadow 
                  className="w-full h-80 border border-border rounded-lg"
                  hideScrollBar={hideScrollBar}
                  size={shadowSize}
                >
                  <div className="p-4 space-y-2">
                    {tradeHistory.map((trade) => (
                      <div 
                        key={trade.id}
                        className="flex items-center justify-between p-3 bg-surface rounded-lg border border-border"
                      >
                        <div className="flex items-center gap-3">
                          <Chip
                            color={trade.type === 'BUY' ? 'success' : 'danger'}
                            size="sm"
                            variant="flat"
                          >
                            {trade.type}
                          </Chip>
                          <div>
                            <div className="font-medium text-text-primary">{trade.symbol}</div>
                            <div className="text-sm text-text-secondary">
                              {trade.quantity} @ ${trade.price}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-text-secondary">{trade.time}</div>
                          <div className={`font-medium ${
                            trade.profit > 0 ? 'text-success' : 'text-danger'
                          }`}>
                            ${trade.profit > 0 ? '+' : ''}${trade.profit.toFixed(2)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollShadow>
              </div>

              {/* News Feed Example */}
              <div className="space-y-3">
                <h3 className="text-lg font-medium text-text-primary">Market News Feed</h3>
                <ScrollShadow 
                  className="w-full h-80 border border-border rounded-lg"
                  hideScrollBar={hideScrollBar}
                  size={shadowSize}
                >
                  <div className="p-4 space-y-3">
                    {newsItems.map((item) => (
                      <div 
                        key={item.id}
                        className="p-3 bg-surface rounded-lg border border-border hover:bg-surface/80 transition-colors"
                      >
                        <div className="font-medium text-text-primary mb-2 leading-tight">
                          {item.title}
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-text-secondary">{item.source}</span>
                          <span className="text-text-tertiary">{item.time}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollShadow>
              </div>

            </div>
          </CardBody>
        </Card>

        {/* Horizontal Scrolling Examples */}
        <Card>
          <CardHeader>
            <div className="space-y-2">
              <h2 className="text-xl font-semibold text-text-primary">Horizontal Scrolling</h2>
              <p className="text-text-secondary">Scroll shadows for horizontally scrollable content</p>
            </div>
          </CardHeader>
          <CardBody className="space-y-6">

            <div className="space-y-3">
              <h3 className="text-lg font-medium text-text-primary">Watchlist Tickers</h3>
              <ScrollShadow 
                orientation="horizontal"
                className="w-full max-w-2xl h-20 border border-border rounded-lg"
                hideScrollBar={hideScrollBar}
                size={shadowSize}
              >
                <div className="flex items-center gap-4 p-4" style={{ width: '1200px' }}>
                  {['AAPL', 'GOOGL', 'MSFT', 'TSLA', 'AMZN', 'NVDA', 'META', 'NFLX', 'AMD', 'INTC', 'CRM', 'ORCL'].map((symbol) => (
                    <div key={symbol} className="shrink-0 p-3 bg-surface rounded-lg border border-border min-w-24 text-center">
                      <div className="font-bold text-text-primary">{symbol}</div>
                      <div className="text-sm text-success">+${(Math.random() * 10 + 1).toFixed(2)}</div>
                    </div>
                  ))}
                </div>
              </ScrollShadow>
            </div>

            <Divider />

            <div className="space-y-3">
              <h3 className="text-lg font-medium text-text-primary">Trading Timeframes</h3>
              <ScrollShadow 
                orientation="horizontal"
                className="w-full max-w-lg h-16 border border-border rounded-lg"
                hideScrollBar={hideScrollBar}
                size={shadowSize}
              >
                <div className="flex items-center gap-2 p-2" style={{ width: '800px' }}>
                  {['1m', '5m', '15m', '30m', '1H', '4H', '1D', '1W', '1M', '3M', '6M', '1Y'].map((timeframe) => (
                    <Button
                      key={timeframe}
                      size="sm"
                      variant="bordered"
                      className="shrink-0"
                    >
                      {timeframe}
                    </Button>
                  ))}
                </div>
              </ScrollShadow>
            </div>

          </CardBody>
        </Card>

        {/* Size and Visibility Examples */}
        <Card>
          <CardHeader>
            <div className="space-y-2">
              <h2 className="text-xl font-semibold text-text-primary">Shadow Customization</h2>
              <p className="text-text-secondary">Different shadow sizes and visibility options</p>
            </div>
          </CardHeader>
          <CardBody className="space-y-6">

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              
              <div className="space-y-3">
                <h3 className="text-base font-medium text-text-primary">Small Shadow (20px)</h3>
                <ScrollShadow 
                  className="w-full h-48 border border-border rounded-lg"
                  size={20}
                  hideScrollBar={hideScrollBar}
                >
                  <div className="p-4 space-y-2">
                    {Array.from({ length: 15 }, (_, i) => (
                      <div key={i} className="p-2 bg-surface rounded border border-border">
                        Portfolio Item #{i + 1}
                      </div>
                    ))}
                  </div>
                </ScrollShadow>
              </div>

              <div className="space-y-3">
                <h3 className="text-base font-medium text-text-primary">Medium Shadow (40px)</h3>
                <ScrollShadow 
                  className="w-full h-48 border border-border rounded-lg"
                  size={40}
                  hideScrollBar={hideScrollBar}
                >
                  <div className="p-4 space-y-2">
                    {Array.from({ length: 15 }, (_, i) => (
                      <div key={i} className="p-2 bg-surface rounded border border-border">
                        Account #{i + 1}
                      </div>
                    ))}
                  </div>
                </ScrollShadow>
              </div>

              <div className="space-y-3">
                <h3 className="text-base font-medium text-text-primary">Large Shadow (60px)</h3>
                <ScrollShadow 
                  className="w-full h-48 border border-border rounded-lg"
                  size={60}
                  hideScrollBar={hideScrollBar}
                >
                  <div className="p-4 space-y-2">
                    {Array.from({ length: 15 }, (_, i) => (
                      <div key={i} className="p-2 bg-surface rounded border border-border">
                        Strategy #{i + 1}
                      </div>
                    ))}
                  </div>
                </ScrollShadow>
              </div>

            </div>
          </CardBody>
        </Card>

        {/* Trading Dashboard Example */}
        <Card>
          <CardHeader>

            <div className="space-y-2">
              <h2 className="text-xl font-semibold text-text-primary">Trading Dashboard Layout</h2>
              <p className="text-text-secondary">Complex layout with multiple scroll shadows</p>
            </div>
          </CardHeader>
          <CardBody className="space-y-6">

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              
              {/* Left Panel - Account Overview */}
              <div className="space-y-3">
                <h3 className="text-lg font-medium text-text-primary">Account Overview</h3>
                <ScrollShadow 
                  className="w-full h-64 border border-border rounded-lg bg-background"
                  size={30}
                >
                  <div className="p-4 space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 bg-success-50 dark:bg-success-900/20 rounded-lg border border-success-200 dark:border-success-800">
                        <div className="text-sm text-success-600 dark:text-success-400">Total P&L</div>
                        <div className="text-xl font-bold text-success-700 dark:text-success-300">+$2,847.50</div>
                      </div>
                      <div className="p-3 bg-primary-50 dark:bg-primary-900/20 rounded-lg border border-primary-200 dark:border-primary-800">
                        <div className="text-sm text-primary-600 dark:text-primary-400">Balance</div>
                        <div className="text-xl font-bold text-primary-700 dark:text-primary-300">$25,847.50</div>
                      </div>
                    </div>
                    
                    {Array.from({ length: 12 }, (_, i) => (
                      <div key={i} className="flex items-center justify-between p-2 bg-surface rounded border border-border">
                        <span className="text-text-secondary">Metric {i + 1}</span>
                        <span className="font-medium text-text-primary">{(Math.random() * 100).toFixed(1)}%</span>
                      </div>
                    ))}
                  </div>
                </ScrollShadow>
              </div>

              {/* Center Panel - Trade Log */}
              <div className="space-y-3">
                <h3 className="text-lg font-medium text-text-primary">Recent Trades</h3>
                <ScrollShadow 
                  className="w-full h-64 border border-border rounded-lg bg-background"
                  size={30}
                >
                  <div className="p-4 space-y-2">
                    {tradeHistory.slice(0, 20).map((trade) => (
                      <div 
                        key={trade.id}
                        className="flex items-center justify-between p-2 bg-surface rounded border border-border text-sm"
                      >
                        <div className="flex items-center gap-2">
                          <Chip
                            color={trade.type === 'BUY' ? 'success' : 'danger'}
                            size="sm"
                            variant="dot"
                          >
                            {trade.symbol}
                          </Chip>
                        </div>
                        <div className="text-text-secondary">{trade.time}</div>
                        <div className={`font-medium ${
                          trade.profit > 0 ? 'text-success' : 'text-danger'
                        }`}>
                          ${trade.profit > 0 ? '+' : ''}${trade.profit.toFixed(0)}
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollShadow>
              </div>

              {/* Right Panel - Market Data */}
              <div className="space-y-3">
                <h3 className="text-lg font-medium text-text-primary">Market Movers</h3>
                <ScrollShadow 
                  className="w-full h-64 border border-border rounded-lg bg-background"
                  size={30}
                >
                  <div className="p-4 space-y-3">
                    {Array.from({ length: 15 }, (_, i) => {
                      const symbols = ['AAPL', 'GOOGL', 'MSFT', 'TSLA', 'AMZN', 'NVDA', 'META'];
                      const change = (Math.random() - 0.5) * 10;
                      return (
                        <div key={i} className="flex items-center justify-between p-2 bg-surface rounded border border-border">
                          <div>
                            <div className="font-medium text-text-primary">{symbols[i % symbols.length]}</div>
                            <div className="text-sm text-text-secondary">${(Math.random() * 500 + 100).toFixed(2)}</div>
                          </div>
                          <div className={`font-medium ${change > 0 ? 'text-success' : 'text-danger'}`}>
                            {change > 0 ? '+' : ''}{change.toFixed(2)}%
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </ScrollShadow>
              </div>

            </div>
          </CardBody>
        </Card>

        {/* Implementation Notes */}
        <Card className="bg-surface">
          <CardBody>
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-text-primary">Implementation Notes</h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-3">
                  <Chip color="primary" size="sm" className="mt-0.5">Theme</Chip>
                  <p className="text-text-secondary">
                    Uses HeroUI's complete theme integration with our custom CSS properties. 
                    Shadow colors automatically adapt to light/dark mode.
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <Chip color="secondary" size="sm" className="mt-0.5">Performance</Chip>
                  <p className="text-text-secondary">
                    Optimized scroll detection with automatic shadow show/hide based on scroll position.
                    Minimal performance impact even with large content.
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <Chip color="success" size="sm" className="mt-0.5">Usage</Chip>
                  <p className="text-text-secondary">
                    Perfect for trade histories, news feeds, watchlists, and any scrollable content 
                    where users need visual indication of additional content availability.
                  </p>
                </div>
              </div>
            </div>
          </CardBody>
        </Card>

      </div>
    </PlaygroundPageLayout>
  );
};